import { useState } from "react";
import { useAuth } from "@/hook/useAuth";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import axiosInstance from "@/utils/axiosInstance";
import useToastNotification from "@/hook/useToastNotification";
import {
	ExtractedData,
	SectionScores,
	SubjectSpecificMarks,
} from "./DocumentUploadOverlay";
import { capitalFirstLetter } from "@/utils/capitalFirstLetter";
import { formatToDDMMYYYY } from "@/utils/formatToDDMMYYY";
import { parseDate } from "@/utils/parseDate";
import {
	getUploadedDocument,
	getUserDetail,
	updateUserDetails,
} from "@/store/feature/user/userApi";
import { saveEligibilityDetails } from "@/store/feature/ai-course-finder/aiCourseApi";
import { parseAddress } from "@/utils/parseAddress";

export interface VerificationJob {
	docId: string;
	documentName: string;
	file: File;
	status:
		| "extracting"
		| "verification_pending"
		| "saving"
		| "uploading"
		| "completed"
		| "error";
	errorMsg?: string;
	extractedData?: ExtractedData | null;
	editErrors?: Record<string, string>;
}

// ── Document types that go through OCR extraction ──────────────────────────
const OCR_SUPPORTED_DOC_TYPES = new Set([
	"10th_marksheet",
	"12th_marksheet",
	"english_proficiency",
	"bachelor_marksheet",
	"passport",
]);

const isOcrSupported = (docType: string): boolean => {
	const normalized = docType.toLowerCase().replace(/\s+/g, "_");
	return OCR_SUPPORTED_DOC_TYPES.has(normalized);
};
// ───────────────────────────────────────────────────────────────────────────

export const useParallelDocumentUpload = (
	applicationId?: string,
	studentId?: string,
) => {
	const dispatch = useAppDispatch();
	const { user } = useAuth();
	const { showToast } = useToastNotification();
	const { userDetails } = useAppSelector((state) => state.userProfile);

	const [jobs, setJobs] = useState<Record<string, VerificationJob>>({});

	const setJobState = (docId: string, updates: Partial<VerificationJob>) => {
		setJobs((prev) => ({
			...prev,
			[docId]: { ...prev[docId], ...updates } as VerificationJob,
		}));
	};

	const removeJob = (docId: string) => {
		setJobs((prev) => {
			const newJobs = { ...prev };
			delete newJobs[docId];
			return newJobs;
		});
	};

	const syncUserDataInBackground = () => {
		if (!user?.uid) return;
		void dispatch(getUploadedDocument(user.uid));
		void dispatch(getUserDetail(user.uid));
	};

	const getPresignedUrl = async (file: File, documentName: string) => {
		const name = documentName.replace(/\s+/g, "_").toLowerCase();
		const payload = [
			{
				fileName: file.name,
				documentType: name.split(".").pop(),
				mimeType: file.type,
				userType: "student",
				userId: user?.uid,
				studentId: studentId,
				fileSize: file.size,
			},
		];

		const response = await axiosInstance.post(
			"/documents/upload/get-presigned-url",
			payload,
		);
		return {
			presignedUrl: response.data[0].presignedUrl,
			documentId: response.data[0].documentId,
		};
	};

	// ── Matches DocumentUploadOverlay: uploads to bucket then patches status ──
	const uploadFileToFirebase = async (file: File, documentName: string) => {
		try {
			const { presignedUrl, documentId } = await getPresignedUrl(
				file,
				documentName,
			);
			await fetch(presignedUrl, {
				method: "PUT",
				body: file,
				headers: {
					"Content-Type": file.type || "application/octet-stream",
				},
			});
			// Use the same PATCH endpoint as DocumentUploadOverlay's markStatusTrue()
			await axiosInstance.patch(`/documents/update/${documentId}`, {
				status: "under_review",
				docType: documentName.toLowerCase().replace(/\s+/g, "_"),
			});
			return documentId;
		} catch (error) {
			console.error("Upload error:", error);
			throw error;
		}
	};
	// ─────────────────────────────────────────────────────────────────────────

	const errorCheck = (documentName: string) => {
		const docNameLower = documentName.toLowerCase();
		if (docNameLower.includes("10th"))
			return "This document does not appear to be a 10th marksheet.";
		if (docNameLower.includes("12th"))
			return "This document does not appear to be a 12th marksheet.";
		if (docNameLower.includes("bachelor"))
			return "This document does not appear to be a valid bachelor's degree marksheet.";
		if (docNameLower.includes("english"))
			return "This document does not appear to be a valid English proficiency test certificate. Please upload a valid IELTS, DUOLINGO, PTE, or TOEFL certificate.";
		if (docNameLower === "passport")
			return "This document does not appear to be a valid passport.";
		return null;
	};

	interface Subject {
		subject: string;
		marks_out_of: string;
		total_marks_obtained: string;
	}

	const calculateBachelorGrading = (extractedData: ExtractedData) => {
		if (
			extractedData?.overall_cgpa !== null &&
			extractedData?.overall_cgpa !== undefined &&
			extractedData?.overall_cgpa !== ""
		) {
			const cgpa = Number(extractedData.overall_cgpa);
			const cgpaOutOf = Number(extractedData?.overall_cgpa_out_of) || 10;
			const gradeTypeMap: { [key: number]: string } = {
				3: "CGPA out of 3",
				4: "CGPA out of 4",
				5: "CGPA out of 5",
				7: "CGPA out of 7",
				9: "CGPA out of 9",
				10: "CGPA out of 10",
			};
			return {
				gradeType: gradeTypeMap[cgpaOutOf] || "CGPA out of 10",
				value: cgpa,
				outOf: cgpaOutOf,
			};
		}
		if (
			extractedData?.overall_marks !== null &&
			extractedData?.overall_marks !== undefined &&
			extractedData?.overall_marks_out_of !== null &&
			extractedData?.overall_marks_out_of !== undefined
		) {
			const marksObtained = Number(extractedData.overall_marks);
			const marksOutOf = Number(extractedData.overall_marks_out_of);
			if (marksOutOf > 0) {
				return {
					gradeType: "Percentage",
					value: Math.round((marksObtained / marksOutOf) * 100 * 100) / 100,
					outOf: 100,
				};
			}
		}
		return { gradeType: "Percentage", value: 0, outOf: 100 };
	};

	const mapStreamToFieldOfStudy = (stream: string): string => {
		const normalizedStream = (stream || "").toLowerCase().trim();
		if (normalizedStream.includes("commerce")) return "Commerce";
		if (
			normalizedStream.includes("non-medical") ||
			normalizedStream.includes("non medical")
		) {
			return "Non-Medical";
		}
		if (
			normalizedStream.includes("super-medical") ||
			normalizedStream.includes("super medical") ||
			normalizedStream.includes("pcmb")
		) {
			return "Super-Medical";
		}
		if (
			normalizedStream.includes("medical") ||
			normalizedStream.includes("biology")
		) {
			return "Medical";
		}
		if (
			normalizedStream.includes("arts") ||
			normalizedStream.includes("humanities")
		) {
			return "Arts-&-Humanities";
		}
		return "";
	};

	const buildSubjectMarks = (
		subjects: Subject[],
		fieldOfStudy: string,
	): SubjectSpecificMarks => {
		const stream = fieldOfStudy.toLowerCase();
		let coreSubjects: string[] = [];

		if (stream === "commerce") {
			coreSubjects = ["Business Studies", "Accounting", "Economics", "English"];
		} else if (stream === "medical") {
			coreSubjects = ["Physics", "Chemistry", "Biology", "English"];
		} else if (stream === "super-medical") {
			coreSubjects = [
				"Physics",
				"Chemistry",
				"Biology",
				"English",
				"Mathematics",
			];
		} else if (stream === "non-medical") {
			coreSubjects = ["Physics", "Chemistry", "Mathematics", "English"];
		} else if (stream === "arts-&-humanities") {
			coreSubjects = ["English"];
		} else {
			coreSubjects = ["Physics", "Chemistry", "English"];
		}

		const normalizeSubject = (name: string) =>
			name
				.toLowerCase()
				.replace(/[^a-z0-9]/g, " ")
				.trim();

		const subjectSpecificMarks: SubjectSpecificMarks = {
			additionalSubject: {},
		};
		const additionalSubjectMap: Record<string, string | number> = {};

		subjects.forEach((subjectItem) => {
			const ocrName = subjectItem.subject || "";
			const marks = subjectItem.total_marks_obtained || "";
			const normalizedOcr = normalizeSubject(ocrName);

			const matchedCore = coreSubjects.find((coreSubject) =>
				normalizeSubject(coreSubject)
					.split(" ")
					.some((word) => word.length > 3 && normalizedOcr.includes(word)),
			);

			if (matchedCore) {
				subjectSpecificMarks[matchedCore] = marks;
			} else {
				const cleanName = ocrName.replace(/^\*+/, "").trim();
				if (cleanName) {
					additionalSubjectMap[cleanName] = marks;
				}
			}
		});

		subjectSpecificMarks.additionalSubject = additionalSubjectMap;
		return subjectSpecificMarks;
	};

	const startExtraction = async (
		docId: string,
		documentName: string,
		file: File,
	): Promise<"completed" | "verification_pending" | "error"> => {
		const docType = documentName.toLowerCase().replace(/\s+/g, "_");

		// ── Skip OCR for non-supported document types ──────────────────────────
		if (!isOcrSupported(docType)) {
			setJobs((prev) => ({
				...prev,
				[docId]: { docId, documentName, file, status: "uploading" },
			}));
			try {
				await uploadFileToFirebase(file, documentName);
				setJobState(docId, { status: "completed" });
				showToast("Document uploaded successfully!", "success");
				syncUserDataInBackground();
				return "completed";
			} catch (e) {
				console.error("Upload error:", e);
				setJobState(docId, {
					status: "error",
					errorMsg: "Failed to upload document.",
				});
				return "error";
			}
		}
		// ───────────────────────────────────────────────────────────────────────

		setJobs((prev) => ({
			...prev,
			[docId]: { docId, documentName, file, status: "extracting" },
		}));

		try {
			const formData = new FormData();
			formData.append("file", file);
			formData.append("document_type", docType);

			const response = await axiosInstance.post(
				`documents/ocr-extraction`,
				formData,
				{
					headers: { "Content-Type": "multipart/form-data" },
				},
			);

			const extracted = response.data.data || [];
			let transformedData: ExtractedData | null = null;
			let err: string | null = null;

			if (docType === "10th_marksheet" || docType === "12th_marksheet") {
				const matchedData = extracted.find(
					(item: any) => item.marksheet_type === docType,
				);
				if (matchedData) {
					const subjects = matchedData.subjects || [];
					const grandTotalObtained = parseInt(
						(matchedData.grand_total_marks_obtained as string) || "",
					);
					const grandTotalOutOf = parseInt(
						(matchedData.grand_total_marks_obtained_out_of as string) || "",
					);
					let totalMarksObtained = 0;
					let totalMarksOutOf = 0;

					if (
						!isNaN(grandTotalObtained) &&
						!isNaN(grandTotalOutOf) &&
						grandTotalOutOf > 0
					) {
						totalMarksObtained = grandTotalObtained;
						totalMarksOutOf = grandTotalOutOf;
					} else {
						subjects.forEach((subject: Subject) => {
							totalMarksObtained += parseInt(subject.total_marks_obtained) || 0;
							totalMarksOutOf += parseInt(subject.marks_out_of) || 0;
						});
					}

					const percentage =
						totalMarksOutOf > 0
							? Math.round((totalMarksObtained / totalMarksOutOf) * 100 * 100) /
								100
							: 0;
					const mappedStream =
						docType === "12th_marksheet"
							? mapStreamToFieldOfStudy(
									(matchedData.stream_or_group as string) || "",
								)
							: "";
					const mappedSubjectMarks =
						docType === "12th_marksheet"
							? buildSubjectMarks(subjects, mappedStream)
							: { additionalSubject: {} };

					transformedData = {
						...matchedData,
						institution: capitalFirstLetter(matchedData.school_name || ""),
						board: matchedData.normalized_board_name || "",
						yearOfPassing: matchedData.passout_year || "",
						studentName: capitalFirstLetter(matchedData.student_name || ""),
						rollNumber: matchedData.roll_number || "",
						examinationName: matchedData.examination_name || "",
						mothersName: capitalFirstLetter(matchedData.mothers_name || ""),
						fathersName: capitalFirstLetter(
							matchedData.fathers_or_guardians_name || "",
						),
						dateOfBirth: matchedData.date_of_birth || "",
						subjects: subjects,
						cgpaOrPercentageValue: percentage.toString(),
						outOf: totalMarksOutOf.toString(),
						totalMarksObtained: totalMarksObtained.toString(),
						fieldOfStudy: mappedStream,
						subjectSpecificMarks: mappedSubjectMarks,
					};
				} else {
					err =
						errorCheck(documentName) ||
						"Could not find matching marksheet data.";
				}
			} else if (docType.includes("bachelor")) {
				if (extracted.length > 0) {
					const bachelorData = extracted[0] as ExtractedData;
					const grading = calculateBachelorGrading(bachelorData);
					transformedData = {
						...bachelorData,
						institution: capitalFirstLetter(
							(
								bachelorData.institution_name ||
								bachelorData.institution ||
								""
							).toString(),
						),
						degreeName: capitalFirstLetter(
							(
								bachelorData.degree_name ||
								bachelorData.degreeName ||
								""
							).toString(),
						),
						yearOfPassing: (
							bachelorData.passout_year ||
							bachelorData.yearOfPassing ||
							""
						).toString(),
						studentName: capitalFirstLetter(
							(
								bachelorData.student_name ||
								bachelorData.studentName ||
								""
							).toString(),
						),
						rollNumber: (
							bachelorData.roll_number ||
							bachelorData.rollNumber ||
							""
						).toString(),
						cgpaOrPercentageValue: grading.value.toString(),
						outOf: grading.outOf.toString(),
					};
				} else {
					err =
						errorCheck(documentName) ||
						"Could not extract bachelor's degree data.";
				}
			} else if (docType === "passport") {
				if (extracted.length > 0) transformedData = extracted[0];
				else
					err = errorCheck(documentName) || "Could not extract passport data.";
			} else if (docType === "english_proficiency") {
				if (extracted.length > 0) {
					const englishData = extracted[0];
					let validTillDate = "";
					if (englishData.test_date) {
						const date = parseDate(String(englishData.test_date));
						if (date && !isNaN(date.getTime())) {
							const validDate = new Date(date);
							validDate.setFullYear(validDate.getFullYear() + 2);
							validTillDate = formatToDDMMYYYY(validDate);
						}
					}
					const sectionScores: SectionScores = {
						reading:
							englishData?.section_scores?.reading ??
							englishData?.reading ??
							"",
						writing:
							englishData?.section_scores?.writing ??
							englishData?.writing ??
							"",
						speaking:
							englishData?.section_scores?.speaking ??
							englishData?.speaking ??
							"",
						listening:
							englishData?.section_scores?.listening ??
							englishData?.listening ??
							"",
					};
					transformedData = {
						...englishData,
						overall_score:
							englishData?.overall_score ?? englishData?.overall ?? "",
						section_scores: sectionScores,
						valid_till_date: validTillDate,
					};
				} else {
					err =
						errorCheck(documentName) ||
						"Could not extract English proficiency data.";
				}
			} else {
				if (extracted.length > 0) transformedData = extracted[0];
				else
					err =
						errorCheck(documentName) ||
						"No data could be extracted from the document.";
			}

			if (transformedData) {
				setJobState(docId, {
					status: "verification_pending",
					extractedData: transformedData,
				});
				return "verification_pending";
			} else {
				setJobState(docId, {
					status: "error",
					errorMsg: err || "Extraction failed",
				});
				return "error";
			}
		} catch (e: any) {
			let errorMessage = "Failed to extract details. Please try again.";
			if (e?.response?.status === 501)
				errorMessage =
					errorCheck(documentName) ||
					"Please Check The Document. Upload Again.";
			else errorMessage = "Please Check The Document. Upload Again.";
			setJobState(docId, { status: "error", errorMsg: errorMessage });
			return "error";
		}
	};

	const parseExtractedName = (extractedName: string) => {
		const normalized = extractedName
			.toLowerCase()
			.trim()
			.replace(/[^a-z\s]/g, "")
			.replace(/\s+/g, " ");
		const parts = normalized.split(" ").filter(Boolean);
		if (parts.length === 0)
			return { firstName: "", middleName: "", lastName: "" };
		if (parts.length === 1)
			return { firstName: parts[0], middleName: "", lastName: "" };
		if (parts.length === 2)
			return { firstName: parts[0], middleName: "", lastName: parts[1] };
		return {
			firstName: parts[0],
			middleName: parts.slice(1, -1).join(" "),
			lastName: parts[parts.length - 1],
		};
	};

	const submitVerification = async (docId: string) => {
		const job = jobs[docId];
		if (!job || !job.extractedData) return false;

		// Guard: block submit if there are validation errors
		const hasErrors = Object.values(job.editErrors || {}).some((e) => e);
		if (hasErrors) return false;

		setJobState(docId, { status: "saving" });

		try {
			await uploadFileToFirebase(job.file, job.documentName);

			const docType = job.documentName.toLowerCase();
			const extractedData = job.extractedData;
			let payload: any = {};

			// ── PASSPORT PAYLOAD ──────────────────────────────────────────────────
			if (docType.includes("passport")) {
				const firstName = capitalFirstLetter(
					extractedData?.given_name?.toString() || "",
				);
				const lastName = capitalFirstLetter(
					(extractedData?.surname || extractedData?.surename)?.toString() || "",
				);
				const parsedName = parseExtractedName(
					`${firstName} ${lastName}`.trim(),
				);
				const parsedAddress = parseAddress(
					extractedData?.address?.toString() || "",
				);
				payload = {
					studentPassportInfo: {
						passportNumber: extractedData?.passport_number || "",
						countryOfIssue: "India",
						expiryDate: formatToDDMMYYYY(extractedData?.date_of_expiry),
					},
					studentBasicInfo: {
						firstName: capitalFirstLetter(parsedName.firstName),
						middleName: capitalFirstLetter(parsedName.middleName),
						lastName: capitalFirstLetter(parsedName.lastName),
						// studentTitle: userDetails?.student_metadata?.student_title || "",
						// fatherTitle: userDetails?.student_metadata?.father_title || "",
						// ✅ Fixed: matches overlay — handles F / M / other string
						gender: extractedData?.gender
							? extractedData.gender === "F"
								? "Female"
								: extractedData.gender === "M"
									? "Male"
									: extractedData.gender
							: userDetails?.gender || null,
						dob: extractedData?.date_of_birth
							? parseDate(extractedData.date_of_birth.toString())
							: userDetails?.dob
								? parseDate(userDetails.dob)
								: null,
						primaryPhoneNumber: userDetails?.primary_phone_number || "",
						secondaryPhoneNumber: userDetails?.secondary_phone_number || "",
						fatherName: capitalFirstLetter(
							extractedData?.name_of_father_or_legal_guardian?.toString() ||
								userDetails?.father_name ||
								"",
						),
					},
					studentAddressInfo: extractedData?.address
						? {
								addressLine1: parsedAddress.addressLine1,
								addressLine2: parsedAddress.addressLine2,
								countryName: parsedAddress.countryName,
								stateName: parsedAddress.stateName,
								cityName: parsedAddress.cityName,
								pincode: parsedAddress.pincode,
							}
						: undefined,
				};
			}

			// ── ENGLISH PROFICIENCY PAYLOAD ───────────────────────────────────────
			if (docType.includes("english")) {
				let validTillDate = "";
				if (extractedData?.test_date) {
					// ✅ Fixed: matches overlay — prefers stored valid_till_date, then
					//    recalculates from test_date as fallback
					if (extractedData.valid_till_date) {
						validTillDate = formatToDDMMYYYY(extractedData.valid_till_date);
					} else {
						const date = parseDate(String(extractedData.test_date));
						if (date && !isNaN(date.getTime())) {
							const validDate = new Date(date);
							validDate.setFullYear(validDate.getFullYear() + 2);
							validTillDate = formatToDDMMYYYY(validDate);
						}
					}
				}

				const sectionScores =
					typeof extractedData?.section_scores === "object" &&
					extractedData?.section_scores !== null
						? (extractedData.section_scores as SectionScores)
						: undefined;

				payload = {
					studentLanguageTestInfo: [
						{
							testName: extractedData?.test_type || "",
							overall: parseFloat(
								typeof extractedData?.overall_score === "number"
									? extractedData.overall_score.toString()
									: typeof extractedData?.overall_score === "string"
										? extractedData.overall_score
										: (extractedData?.overall as string) || "0",
							),
							reading: parseFloat(
								sectionScores?.reading ||
									(extractedData?.reading as string) ||
									"0",
							),
							writing: parseFloat(
								sectionScores?.writing ||
									(extractedData?.writing as string) ||
									"0",
							),
							speaking: parseFloat(
								sectionScores?.speaking ||
									(extractedData?.speaking as string) ||
									"0",
							),
							listening: parseFloat(
								sectionScores?.listening ||
									(extractedData?.listening as string) ||
									"0",
							),
							validTillDate: validTillDate,
						},
					],
				};
			}

			// ── MOI PROFICIENCY PAYLOAD ───────────────────────────────────────────
			if (docType.includes("moi")) {
				payload = {
					studentLanguageTestInfo: [
						{
							testName: "I-DON'T-HAVE-ANY",
							boardName: extractedData?.board_name || "",
							englishMarks: Number(extractedData?.english_marks) || 0,
							overall: Number(extractedData?.english_marks) || 0,
						},
					],
				};
			}

			// ── 10TH MARKSHEET PAYLOAD ────────────────────────────────────────────
			if (docType.includes("10th")) {
				const parsedName = parseExtractedName(
					(
						extractedData?.studentName ||
						extractedData?.student_name ||
						""
					).toString(),
				);
				const outOfValue =
					Number(extractedData?.outOf || extractedData?.overall_marks_out_of) ||
					100;
				const dobValue = (
					extractedData?.dateOfBirth ||
					extractedData?.date_of_birth ||
					""
				).toString();

				payload = {
					studentQualificationInfo: [
						{
							qualificationLevel: "10th",
							instituteName: extractedData?.institution || "",
							boardName: extractedData?.board || "",
							completionYear: extractedData?.yearOfPassing || "",
							gradeType: "Percentage",
							cgpaOrPercentageValue:
								Number(extractedData?.cgpaOrPercentageValue) || 0,
							outOf: outOfValue,
							subjectSpecificMarks: extractedData?.subjectSpecificMarks || {
								additionalSubject: {},
							},
						},
					],
					studentBasicInfo: {
						firstName: capitalFirstLetter(parsedName.firstName),
						middleName: capitalFirstLetter(parsedName.middleName),
						lastName: capitalFirstLetter(parsedName.lastName),
						// studentTitle: userDetails?.student_metadata?.student_title || "",
						// fatherTitle: userDetails?.student_metadata?.father_title || "",
						gender: userDetails?.gender || null,
						dob: dobValue
							? parseDate(dobValue)
							: userDetails?.dob
								? parseDate(userDetails.dob)
								: null,
						primaryPhoneNumber: userDetails?.primary_phone_number || "",
						secondaryPhoneNumber: userDetails?.secondary_phone_number || "",
						fatherName: capitalFirstLetter(
							(
								extractedData?.fathersName ||
								extractedData?.fathers_or_guardians_name ||
								userDetails?.father_name ||
								""
							).toString(),
						),
					},
				};
			}

			// ── 12TH MARKSHEET PAYLOAD ────────────────────────────────────────────
			if (docType.includes("12th")) {
				const parsedName = parseExtractedName(
					(
						extractedData?.studentName ||
						extractedData?.student_name ||
						""
					).toString(),
				);
				const outOfValue =
					Number(extractedData?.outOf || extractedData?.overall_marks_out_of) ||
					100;
				const dobValue = (
					extractedData?.dateOfBirth ||
					extractedData?.date_of_birth ||
					""
				).toString();

				payload = {
					studentQualificationInfo: [
						{
							qualificationLevel: "12th",
							instituteName: extractedData?.institution || "",
							boardName: extractedData?.board || "",
							completionYear: extractedData?.yearOfPassing || "",
							gradeType: "Percentage",
							cgpaOrPercentageValue:
								Number(extractedData?.cgpaOrPercentageValue) || 0,
							outOf: outOfValue,
							fieldOfStudy: extractedData?.fieldOfStudy || "",
							subjectSpecificMarks: extractedData?.subjectSpecificMarks || {
								additionalSubject: {},
							},
						},
					],
					studentBasicInfo: {
						firstName: capitalFirstLetter(parsedName.firstName),
						middleName: capitalFirstLetter(parsedName.middleName),
						lastName: capitalFirstLetter(parsedName.lastName),
						studentTitle: userDetails?.student_metadata?.student_title || "",
						fatherTitle: userDetails?.student_metadata?.father_title || "",
						gender: userDetails?.gender || null,
						dob: dobValue
							? parseDate(dobValue)
							: userDetails?.dob
								? parseDate(userDetails.dob)
								: null,
						primaryPhoneNumber: userDetails?.primary_phone_number || "",
						secondaryPhoneNumber: userDetails?.secondary_phone_number || "",
						fatherName: capitalFirstLetter(
							(
								extractedData?.fathersName ||
								extractedData?.fathers_or_guardians_name ||
								userDetails?.father_name ||
								""
							).toString(),
						),
					},
				};
			}

			// ── BACHELOR'S DEGREE PAYLOAD ─────────────────────────────────────────
			if (docType.includes("bachelor")) {
				const gradeInfo = calculateBachelorGrading(extractedData || {});
				payload = {
					studentQualificationInfo: [
						{
							qualificationLevel: "Bachelor",
							instituteName:
								extractedData?.institution_name ||
								extractedData?.institution ||
								"",
							qualificationName:
								extractedData?.degree_name ||
								extractedData?.degreeName ||
								"",
							completionYear: String(
								extractedData?.passout_year ||
									extractedData?.yearOfPassing ||
									"",
							),
							gradeType: gradeInfo.gradeType,
							cgpaOrPercentageValue: Number(gradeInfo.value) || 0,
							outOf: Number(gradeInfo.outOf) || 100,
						},
					],
				};
			}

			// ── DISPATCH UPDATE ───────────────────────────────────────────────────
			if (Object.keys(payload).length > 0) {
				try {
					await dispatch(
						updateUserDetails({ payload, id: studentId || user?.uid || "" }),
					);

					// Auto-sync savedEligibility when academic or English docs are uploaded
					const effectiveUserId = studentId || user?.uid || "";
					if (effectiveUserId && (docType.includes("10th") || docType.includes("12th") || docType.includes("bachelor") || docType.includes("english") || docType.includes("moi"))) {
						try {
							const qualInfo = payload.studentQualificationInfo?.[0];
							const langInfo = payload.studentLanguageTestInfo?.[0];
							const eligibilityPayload: Record<string, string | number> = {};

							if (qualInfo) {
								eligibilityPayload.pastQualification = qualInfo.qualificationLevel ?? "";
								eligibilityPayload.courseStream = qualInfo.fieldOfStudy ?? "";
								eligibilityPayload.qualificationYear = qualInfo.completionYear ?? "";
								eligibilityPayload.gradingSystem = qualInfo.gradeType ?? "Percentage";
								eligibilityPayload.pastQualificationPercentage = Number(qualInfo.cgpaOrPercentageValue) || 0;
							}

							if (langInfo) {
								const isNoTest = langInfo.testName?.toUpperCase().replace(/[^A-Z]/g, "") === "IDONTHAVEANY";
								if (isNoTest) {
									eligibilityPayload.test = "";
									eligibilityPayload.boardName = langInfo.boardName ?? "";
									eligibilityPayload.overall = langInfo.englishMarks ?? 0;
								} else {
									eligibilityPayload.test = langInfo.testName ?? "";
									eligibilityPayload.listening = langInfo.listening ?? 0;
									eligibilityPayload.reading = langInfo.reading ?? 0;
									eligibilityPayload.writing = langInfo.writing ?? 0;
									eligibilityPayload.speaking = langInfo.speaking ?? 0;
									eligibilityPayload.overall = langInfo.overall ?? 0;
								}
							}

							if (Object.keys(eligibilityPayload).length > 0) {
								dispatch(saveEligibilityDetails({ eligibility: eligibilityPayload, studentId: effectiveUserId }));
							}
						} catch {
							// Best-effort — don't block document upload success
						}
					}
				} catch (error) {
					console.error("Updating student details failed:", error);
					showToast("Failed to update student details.", "error");
				}
			}

			setJobState(docId, {
				status: "completed",
				extractedData: null,
				editErrors: {},
			});
			showToast("Document uploaded successfully!", "success");
			syncUserDataInBackground();
			return true;
		} catch (error) {
			console.error("Details update failed:", error);
			showToast("Failed to update details.", "error");
			setJobState(docId, { status: "verification_pending" });
			return false;
		}
	};

	const updateExtractedData = (docId: string, field: string, value: string) => {
		setJobs((prev) => {
			const job = prev[docId];
			if (!job || !job.extractedData) return prev;

			const extractedData = { ...job.extractedData };
			const sectionFields = ["listening", "reading", "writing", "speaking"];

			if (sectionFields.includes(field)) {
				extractedData.section_scores = {
					...(extractedData.section_scores as any),
					[field]: value,
				};
				(extractedData as any)[field] = value;
			} else if (field === "test_date") {
				// ✅ Matches overlay: auto-calculate valid_till_date when test_date changes
				let validTillDate = "";
				if (value) {
					const date = parseDate(value);
					if (date && !isNaN(date.getTime())) {
						const validDate = new Date(date);
						validDate.setFullYear(validDate.getFullYear() + 2);
						validTillDate = formatToDDMMYYYY(validDate);
					}
				}
				(extractedData as any)[field] = value;
				extractedData.valid_till_date = validTillDate;
			} else {
				(extractedData as any)[field] = value;
			}

			return {
				...prev,
				[docId]: { ...job, extractedData },
			};
		});
	};

	return {
		jobs,
		startExtraction,
		setJobState,
		removeJob,
		submitVerification,
		updateExtractedData,
	};
};
